import { useState, useEffect } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import AnimatedCounter from './AnimatedCounter';
import TrophyShowcase from './TrophyShowcase';
import './GoalProgressBars.css';

const GoalProgressBars = ({ goals = [], className = '' }) => {
  const { lastGlobalStatsUpdate } = useWebSocket();
  const [localGoals, setLocalGoals] = useState(goals);
  const [isUpdating, setIsUpdating] = useState(false);

  // Update goals when new data comes in via WebSocket
  useEffect(() => {
    if (lastGlobalStatsUpdate?.stats?.goals) {
      setIsUpdating(true);
      setLocalGoals(lastGlobalStatsUpdate.stats.goals);
      
      // Remove updating state after animation completes
      const timer = setTimeout(() => setIsUpdating(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [lastGlobalStatsUpdate]);

  // Update goals when props change
  useEffect(() => {
    setLocalGoals(goals);
  }, [goals]);

  // Filter to get the three primary goals in the specified order
  const primaryGoals = localGoals
    .filter(goal => ['total_chapters_read', 'total_readers', 'total_bible_completions'].includes(goal.key))
    .sort((a, b) => {
      const order = ['total_chapters_read', 'total_readers', 'total_bible_completions'];
      return order.indexOf(a.key) - order.indexOf(b.key);
    });

  if (primaryGoals.length === 0) {
    return (
      <div className={`goal-progress-bars ${className}`}>
        <div className="goals-loading">
          <div className="loading-spinner"></div>
          <p>Loading community goals...</p>
        </div>
      </div>
    );
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const getGoalColor = (key) => {
    switch (key) {
      case 'total_chapters_read':
        return 'var(--color-primary)';
      case 'total_readers':
        return 'var(--color-secondary)';
      case 'total_bible_completions':
        return 'var(--color-accent)';
      default:
        return 'var(--color-primary)';
    }
  };

  const renderGoalComponent = (goal) => {
    const getGridClass = (key) => {
      switch (key) {
        case 'total_chapters_read':
          return 'goal-chapters';
        case 'total_readers':
          return 'goal-readers';
        case 'total_bible_completions':
          return 'goal-completions';
        default:
          return '';
      }
    };

    switch (goal.key) {
      case 'total_readers':
        return (
          <div key={goal.id} className={getGridClass(goal.key)}>
            <AnimatedCounter
              value={goal.currentValue}
              title="Readers"
              icon="👥"
              isUpdating={isUpdating}
            />
          </div>
        );
      
      case 'total_bible_completions':
        return (
          <div key={goal.id} className={getGridClass(goal.key)}>
            <TrophyShowcase
              value={goal.currentValue}
              title="Bible completions"
              isUpdating={isUpdating}
            />
          </div>
        );
      
      case 'total_chapters_read':
      default:
        return (
          <div key={goal.id} className={getGridClass(goal.key)}>
            <div 
              className={`goal-bar ${goal.isCompleted ? 'completed' : ''} ${isUpdating ? 'updating' : ''}`}
              title={goal.description || goal.name}
            >
              <div className="goal-header">
                <h3 className="goal-name">{goal.name}: {formatNumber(goal.currentValue)}</h3>
                {goal.isCompleted && (
                  <div className="goal-badge">
                    <span>🎉</span>
                  </div>
                )}
              </div>
              
              <div className="goal-progress">
                <div className="progress-section">
                  <div className="progress-percentage-inline">
                    <span>{goal.progress}%</span>
                  </div>
                  
                  <div className="progress-bar-container">
                    {/* Current value indicator with arrow */}
                    <div 
                      className="current-indicator"
                      style={{ 
                        left: `${Math.min(goal.progress, 100)}%`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      <div className="arrow-down">▼</div>
                    </div>
                      
                      {/* Progress bar */}
                      <div 
                        className="progress-bar-fill"
                        style={{ 
                          width: `${goal.progress}%`,
                          backgroundColor: getGoalColor(goal.key)
                        }}
                      />
                      <div className="progress-bar-background" />
                      
                      {/* Target value at the end */}
                    <div className="target-indicator">
                      <div className="target-value">{formatNumber(goal.targetValue)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`goal-progress-bars ${className} ${isUpdating ? 'updating' : ''}`}>
      <div className="goals-container mixed-layout">
        {primaryGoals.map((goal) => renderGoalComponent(goal))}
      </div>
      
      {isUpdating && (
        <div className="live-update-indicator">
          <span className="pulse-dot"></span>
          <span>Live updates active</span>
        </div>
      )}
    </div>
  );
};

export default GoalProgressBars;
